import React, { useState, useEffect, useCallback, memo } from 'react';
import PropTypes from 'prop-types';
// Lucide icons
import { 
  Activity,
  AlertTriangle,
  Award,
  BarChart3, 
  Calendar, 
  ChevronDown,
  ChevronUp,
  Clipboard, 
  PlusCircle, 
  RefreshCw, 
  Settings,
  TrendingUp, 
  User
} from 'lucide-react';
import { trackPageView, AnalyticsEvents } from '../../utils/analytics';
import { formatBattingStat } from '../../utils/calculations';

// Dashboard components
import MilestonesCard from './MilestonesCard';
import PerformanceTrends from './PerformanceTrends';
import QuickEntry from './QuickEntry';
import RecentGames from './RecentGames';
import SlumpDetection from './SlumpDetection';
import GettingStartedCard from './GettingStartedCard';

// Shared components
import HeaderWithSeasonSelector from '../shared/HeaderWithSeasonSelector';
import StatCard from '../shared/StatCard';
import WeatherCard from '../shared/WeatherCard';
import ErrorBoundary from '../shared/ErrorBoundary';

// Hooks and utilities
import { usePlayer } from '../../context/PlayerContext';
import { useStats } from '../../hooks/useStats';
import { formatInnings } from '../../utils/formatters';

// Icon constants for consistent usage throughout the component
const ICONS = {
  DASHBOARD: Activity,
  TRENDS: TrendingUp,
  BATTING: Clipboard,
  PITCHING: TrendingUp,
  GAMES: Calendar,
  PROFILE: User,
  STATS: BarChart3,
  QUICK_ENTRY: PlusCircle,
  REFRESH: RefreshCw,
  EXPAND: ChevronDown,
  COLLAPSE: ChevronUp,
  ACHIEVEMENTS: Award,
  SETTINGS: Settings,
  ALERT: AlertTriangle
};

// Layout constants
const SPACING = {
  SECTION: 'mb-6',
  CARD: 'mb-4',
  GRID: 'gap-3',
  TITLE: 'mb-2',
  CONTENT: 'mb-3',
};

// Animation constants
const TRANSITIONS = {
  DEFAULT: 'transition-all duration-300 ease-in-out',
  FAST: 'transition-all duration-150 ease-in-out',
  SLOW: 'transition-all duration-500 ease-in-out',
  SECTION: 'transition-all duration-300 ease-in-out will-change-auto',
};

// Custom hook for pull-to-refresh functionality
const usePullToRefresh = (onRefresh) => {
  useEffect(() => {
    let touchStart = 0;
    let touchEnd = 0;
    let isMounted = true; // Flag to prevent memory leaks
    const threshold = 150; // Minimum distance to trigger refresh
    const maxScroll = 5; // Maximum scroll position to allow pull

    const handleTouchStart = (e) => {
      touchStart = e.targetTouches[0].clientY;
    };

    const handleTouchMove = (e) => {
      touchEnd = e.targetTouches[0].clientY;
    };

    const handleTouchEnd = () => {
      if (isMounted && window.scrollY <= maxScroll && touchStart && (touchEnd - touchStart > threshold)) {
        onRefresh();
      }
      touchStart = 0;
      touchEnd = 0;
    };
    
    document.addEventListener('touchstart', handleTouchStart, false);
    document.addEventListener('touchmove', handleTouchMove, false);
    document.addEventListener('touchend', handleTouchEnd, false);

    return () => {
      isMounted = false; // Prevent memory leaks
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [onRefresh]);
};
// Memoized subcomponents for performance
const MemoizedPerformanceTrends = memo(PerformanceTrends);
const MemoizedRecentGames = memo(RecentGames);
const MemoizedMilestonesCard = memo(MilestonesCard);
const MemoizedSlumpDetection = memo(SlumpDetection);

// Section component for collapsible sections
const Section = memo(({ title, children, defaultExpanded = true, icon: Icon }) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  
  return (
    <div className={SPACING.SECTION}>
      <div 
        className="flex justify-between items-center mb-3 cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center">
          {Icon && <Icon size={20} className="mr-2 text-blue-600" />}
          <h3 className="text-lg font-medium">{title}</h3>
        </div>
        <button 
          className="text-gray-500 dark:text-gray-400 p-1 rounded-full hover:bg-gray-100 dark:hover:bg-dark-400 active:bg-gray-200 dark:active:bg-dark-500"
          aria-label={expanded ? "Collapse section" : "Expand section"}
        >
          {expanded ? <ICONS.COLLAPSE size={20} /> : <ICONS.EXPAND size={20} />}
        </button>
      </div>
      
      <div 
        className={`${TRANSITIONS.SECTION} overflow-hidden ${expanded ? 'max-h-[5000px] opacity-100' : 'max-h-0 opacity-0'}`}
        style={{
          transform: expanded ? 'translateY(0)' : 'translateY(-10px)',
          visibility: expanded ? 'visible' : 'hidden',
          transitionDelay: expanded ? '0ms' : '150ms'
        }}
      >
        {children}
      </div>
    </div>
  );
});

// PropTypes for Section component
Section.propTypes = {
  title: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  defaultExpanded: PropTypes.bool,
  icon: PropTypes.elementType
};

// The Section component has been moved above to fix the PropTypes reference
const Dashboard = () => {
  const { playerData, showQuickEntry, setShowQuickEntry, isLoading, fetchPlayerData, activeSeason, viewingAllSeasons, selectedPlayerId, dataVersion } = usePlayer();
  const [refreshing, setRefreshing] = useState(false);

  // Use the useStats hook to get consistent stats (same as BattingStats/PitchingStats pages)
  const {
    battingStats: hookBattingStats,
    pitchingStats: hookPitchingStats
  } = useStats(selectedPlayerId, activeSeason, viewingAllSeasons, dataVersion, playerData);
  
  // Track page view when component mounts
  useEffect(() => {
    trackPageView(AnalyticsEvents.DASHBOARD_VIEWED, {
      timestamp: new Date().toISOString()
    });
  }, []);
  
  // Handle refresh action
  const handleRefresh = useCallback(() => {
    setRefreshing(true);
    // Simulate a network request with timeout
    setTimeout(() => {
      fetchPlayerData();
      setRefreshing(false);
    }, 800);
  }, [fetchPlayerData]);
  
  // Setup pull-to-refresh
  usePullToRefresh(handleRefresh);
  
  // Find the next upcoming game from player's schedule
  const findNextUpcomingGame = () => {
    // Create arrays even if playerData properties are undefined
    const recentGames = playerData?.recentGames || [];
    const scheduledGames = playerData?.scheduledGames || [];
    
    // If both arrays are empty, return null
    if (recentGames.length === 0 && scheduledGames.length === 0) {
      return null;
    }
    
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Set to beginning of day for date comparison
    
    // Create a combined array of all games
    const allGames = [...recentGames, ...scheduledGames];
    
    // Filter games based on the active season if one is selected
    const seasonFilteredGames = !viewingAllSeasons && activeSeason 
      ? allGames.filter(game => game.seasonId === activeSeason.id)
      : allGames;
    
    // Filter upcoming games (where date is in the future)
    // and sort by date (closest first)
    const upcomingGames = seasonFilteredGames
      .filter(game => {
        if (!game.date) return false;
        
      // Create date objects with noon time to avoid timezone issues
      const gameDate = new Date(game.date + 'T12:00:00');
      return gameDate >= today && (game.status === 'upcoming' || !game.status);
      })
      .sort((a, b) => {
        // Create date objects with noon time to avoid timezone issues
        const dateA = new Date(a.date + 'T12:00:00');
        const dateB = new Date(b.date + 'T12:00:00');
        return dateA - dateB;
      });
    
    return upcomingGames.length > 0 ? upcomingGames[0] : null;
  };
  
  const nextGame = findNextUpcomingGame();
  
  // Calculate trends (in a real app, these would come from actual data analysis)
  const calculateTrend = (current, previous) => {
    if (!previous) return null;
    return Math.round(((current - previous) / previous) * 100);
  };
  
  // Use stats from useStats hook for consistency with BattingStats/PitchingStats pages
  // The hook handles season vs career logic internally
  const battingStats = hookBattingStats;
  const pitchingStats = hookPitchingStats;

  // Log the stats being used for debugging
  console.log('Dashboard using stats from useStats hook:', {
    activeSeason: activeSeason?.name,
    viewingAllSeasons,
    battingStats: {
      games: battingStats?.games || 0,
      atBats: battingStats?.atBats || 0,
      hits: battingStats?.hits || 0,
      average: battingStats?.average || 0
    }
  });
  
  // Stats cards data
  const battingCards = [
    {
      id: 'avg',
      title: 'AVG',
      value: formatBattingStat(battingStats?.average) || '.000',
      icon: ICONS.STATS,
      trend: calculateTrend(battingStats?.average, battingStats?.previousAverage),
      trendLabel: "vs last 10 games"
    },
    {
      id: 'obp',
      title: 'OBP',
      value: formatBattingStat(battingStats?.obp) || '.000',
      icon: ICONS.STATS,
      trend: calculateTrend(battingStats?.obp, battingStats?.previousObp),
      trendLabel: "vs last 10 games"
    },
    {
      id: 'slg',
      title: 'SLG',
      value: formatBattingStat(battingStats?.slg) || '.000',
      icon: ICONS.STATS,
      trend: calculateTrend(battingStats?.slg, battingStats?.previousSlg),
      trendLabel: "vs last 10 games"
    },
    {
      id: 'ops',
      title: 'OPS',
      value: formatBattingStat(battingStats?.ops) || '.000',
      icon: ICONS.STATS,
      trend: calculateTrend(battingStats?.ops, battingStats?.previousOps),
      trendLabel: "vs last 10 games"
    },
    { 
      id: 'rbi',
      title: 'RBI', 
      value: battingStats?.rbi || 0,
      icon: ICONS.STATS,
      trend: calculateTrend(battingStats?.rbi, battingStats?.previousRbi),
      trendLabel: "Season Total"
    }
  ];
  
  const pitchingCards = [
    { 
      id: 'era',
      title: 'ERA', 
      value: pitchingStats?.era?.toFixed(2) || '0.00',
      icon: ICONS.STATS,
      trend: calculateTrend(pitchingStats?.era, pitchingStats?.previousEra),
      // For ERA, a negative trend is good
      trendDirection: 'inverted'
    },
    { 
      id: 'whip',
      title: 'WHIP', 
      value: pitchingStats?.whip?.toFixed(2) || '0.00',
      icon: ICONS.STATS,
      trend: calculateTrend(pitchingStats?.whip, pitchingStats?.previousWhip),
      // For WHIP, a negative trend is good
      trendDirection: 'inverted'
    },
    { 
      id: 'wl',
      title: 'W-L', 
      value: `${pitchingStats?.wins || 0}-${pitchingStats?.losses || 0}`,
      icon: ICONS.STATS
    },
    { 
      id: 'ip',
      title: 'IP', 
      value: formatInnings(pitchingStats?.inningsPitched) || '0.0',
      icon: ICONS.STATS
    }
  ];
  return (
    <>
      {/* Header with refresh button and season selector */}
      <HeaderWithSeasonSelector 
        title="Dashboard" 
        subtitle="Track your baseball performance" 
        actions={[
          { 
            icon: ICONS.REFRESH, 
            onClick: handleRefresh, 
            label: 'Refresh data',
            className: refreshing ? 'animate-spin' : ''
          },
          { 
            icon: ICONS.SETTINGS, 
            onClick: () => {/* Handle settings */}, 
            label: 'Settings' 
          }
        ]}
      />
      
      <div className="p-4 pb-24">
        {/* Pull to refresh indicator */}
        {refreshing && (
          <div className="text-center text-sm text-gray-500 dark:text-gray-400 -mt-2 mb-4 flex items-center justify-center">
            <ICONS.REFRESH size={14} className="animate-spin mr-2" /> Refreshing...
          </div>
        )}
        
        {/* Getting Started Guide for new users */}
        <ErrorBoundary>
          <GettingStartedCard />
        </ErrorBoundary>
      
        {/* Quick Entry Modal */}
        {showQuickEntry && (
          <div className="mb-4">
            <ErrorBoundary>
              <QuickEntry onClose={() => setShowQuickEntry(false)} />
            </ErrorBoundary>
          </div>
        )}
        
        {/* Quick Entry Button - Fixed Action Button */}
        <button 
          onClick={() => setShowQuickEntry(true)}
          className="fixed right-4 bottom-20 z-10 bg-blue-600 text-white p-3 rounded-full shadow-lg hover:bg-blue-700 active:bg-blue-800 transition-all transform hover:scale-105 active:scale-95"
          aria-label="Quick Entry"
        >
          <ICONS.QUICK_ENTRY size={24} />
        </button>
        <Section title="Player Summary" icon={ICONS.PROFILE} defaultExpanded={true}>
          <ErrorBoundary>
            <StatCard
              loading={isLoading}
              className="mb-4"
              bgColor="bg-gradient-to-r from-blue-500 to-blue-600"
              gradient="text-white"
            >
              <article itemScope itemType="http://schema.org/Person">
                <div className="flex justify-between items-center mb-4">
                  <div>
                    <h3 className="font-bold text-lg" itemProp="name">{playerData?.name || 'Player Name'}</h3>
                    <p className="text-gray-100">
                      Position: <span itemProp="jobTitle">
                        {playerData?.position || 'Unknown'}
                        {playerData?.secondaryPosition && ` / ${playerData.secondaryPosition}`}
                        {playerData?.tertiaryPosition && ` / ${playerData.tertiaryPosition}`}
                      </span>
                    </p>
                    <p className="text-gray-100">Leagues: <span itemProp="memberOf">{playerData?.leagues?.join(', ') || 'None'}</span></p>
                  </div>
                  <div className="rounded-full h-16 w-16 flex items-center justify-center bg-white/20 text-white text-2xl font-bold">
                    {playerData?.number || '#'}
                  </div>
                </div>
              </article>
            </StatCard>
            
            {/* Batting Stats Grid */}
            <h4 className="text-md font-medium mb-2 flex items-center">
              <ICONS.BATTING size={16} className="mr-1" aria-hidden="true" /> Batting Statistics
            </h4>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3 mb-4" role="group" aria-label="Batting Statistics">
              {battingCards.map((card) => (
                <StatCard
                  key={`batting-${card.id}`}
                  title={card.title}
                  value={card.value}
                  icon={card.icon}
                  size="compact"
                  loading={isLoading}
                  trend={card.trend}
                  trendLabel={card.trendLabel}
                />
              ))}
            </div>
            
            {/* On Pace Section - Only show if we have games played */}
            {battingStats && battingStats.games > 0 && (
              <div className="mb-4">
                <h4 className="text-md font-medium mb-2 flex items-center">
                  <TrendingUp size={16} className="mr-1" aria-hidden="true" /> On Pace For
                </h4>
                <div className="p-4 bg-gradient-to-r from-green-50 to-blue-50 dark:from-green-900/20 dark:to-blue-900/20 border border-green-200 dark:border-green-800/30 rounded-md shadow-sm">
                  {(() => {
                    // Use the totalGames value from the season, or fall back to scheduled games or default
                    const seasonTotalGames = activeSeason?.totalGames || playerData?.scheduledGames?.length || 162;
                    const gamesPlayed = battingStats.games || 0;
                    
                    // Calculate pace multiplier
                    const paceMultiplier = seasonTotalGames / gamesPlayed;
                    
                    // Stats to project - Counting stats
                    const countingStats = [
                      { 
                        label: 'Hits', 
                        value: Math.round(battingStats.hits * paceMultiplier),
                        current: battingStats.hits
                      },
                      { 
                        label: 'Home Runs', 
                        value: Math.round(battingStats.homeRuns * paceMultiplier),
                        current: battingStats.homeRuns
                      },
                      { 
                        label: 'RBI', 
                        value: Math.round(battingStats.rbi * paceMultiplier),
                        current: battingStats.rbi
                      },
                      { 
                        label: 'Runs', 
                        value: Math.round(battingStats.runs * paceMultiplier),
                        current: battingStats.runs
                      },
                      { 
                        label: 'Strikeouts', 
                        value: Math.round(battingStats.strikeouts * paceMultiplier),
                        current: battingStats.strikeouts
                      }
                    ];
                    
                    // Rate stats - these don't use the multiplier directly
                    const rateStats = [
                      {
                        label: 'AVG',
                        value: battingStats.average,
                        format: formatBattingStat
                      },
                      {
                        label: 'OBP',
                        value: battingStats.obp,
                        format: formatBattingStat
                      },
                      {
                        label: 'SLG',
                        value: battingStats.slg,
                        format: formatBattingStat
                      }
                    ];
                    
                    return (
                      <>
                        <div className="mb-4">
                          <h5 className="text-sm font-semibold text-green-700 dark:text-green-400 mb-2 border-b border-green-200 dark:border-green-800/30 pb-1">
                            Season Totals
                          </h5>
                          <div className="grid grid-cols-3 md:grid-cols-5 gap-3">
                            {countingStats.map((stat, index) => (
                              <div key={index} className="bg-white dark:bg-dark-200 p-2 rounded-md shadow-sm border border-green-100 dark:border-green-900/20">
                                <div className="text-lg font-bold text-green-700 dark:text-green-400 text-center">
                                  {stat.value}
                                </div>
                                <div className="text-xs text-green-600 dark:text-green-500 text-center">
                                  {stat.label}
                                </div>
                                <div className="text-xs text-gray-500 dark:text-gray-400 text-center mt-1">
                                  Current: {stat.current}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                        
                        <div>
                          <h5 className="text-sm font-semibold text-blue-700 dark:text-blue-400 mb-2 border-b border-blue-200 dark:border-blue-800/30 pb-1">
                            Rate Statistics
                          </h5>
                          <div className="grid grid-cols-3 gap-3">
                            {rateStats.map((stat, index) => (
                              <div key={index} className="bg-white dark:bg-dark-200 p-2 rounded-md shadow-sm border border-blue-100 dark:border-blue-900/20">
                                <div className="text-lg font-bold text-blue-700 dark:text-blue-400 text-center">
                                  {stat.format(stat.value)}
                                </div>
                                <div className="text-xs text-blue-600 dark:text-blue-500 text-center">
                                  {stat.label}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                        
                        <div className="mt-3 text-xs text-gray-600 dark:text-gray-400 text-center">
                          Projections based on {battingStats.games} games played of {seasonTotalGames} game season
                        </div>
                      </>
                    );
                  })()}
                </div>
              </div>
            )}
            
            {/* Pitching Stats Grid - Only show if there are pitching stats */}
            {pitchingStats && (pitchingStats.inningsPitched > 0 || pitchingStats.games > 0 || pitchingStats.strikeouts > 0) ? (
              <>
                <h4 className="text-md font-medium mb-2 flex items-center">
                  <ICONS.PITCHING size={16} className="mr-1" aria-hidden="true" /> Pitching Statistics
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3" role="group" aria-label="Pitching Statistics">
                  {pitchingCards.map((card) => (
                    <StatCard
                      key={`pitching-${card.id}`}
                      title={card.title}
                      value={card.value}
                      icon={card.icon}
                      size="compact"
                      loading={isLoading}
                      trend={card.trend}
                      trendLabel={card.trendLabel}
                      trendDirection={card.trendDirection}
                    />
                  ))}
                </div>
              </>
            ) : (
              <>
                <h4 className="text-md font-medium mb-2 flex items-center">
                  <ICONS.PITCHING size={16} className="mr-1" aria-hidden="true" /> Pitching Statistics
                </h4>
                <div className="p-4 text-center bg-gray-50 dark:bg-dark-300 rounded-lg border border-gray-200 dark:border-dark-400">
                  <p className="text-gray-600 dark:text-darkText-secondary">No pitching statistics available</p>
                </div>
              </>
            )}
          </ErrorBoundary>
        </Section>
        
        {/* Upcoming Game with Weather */}
        <Section title="Upcoming Game" icon={ICONS.GAMES} defaultExpanded={true}>
          <ErrorBoundary>
            <StatCard className="mb-0">
              {nextGame ? (
                <>
                  <div className="flex justify-between mb-2">
                    <div className="font-bold">vs {nextGame.opponent}</div>
                    <div className="text-xs bg-green-100 dark:bg-green-800 text-green-800 dark:text-green-100 px-2 py-1 rounded">
                      {nextGame.isHome ? 'HOME' : 'AWAY'}
                    </div>
                  </div>
                  <div className="text-sm text-gray-700 dark:text-gray-300 mb-3">
                    {new Date(nextGame.date + 'T12:00:00').toLocaleDateString('en-US', { 
                      weekday: 'long', 
                      month: 'short', 
                      day: 'numeric' 
                    })} • {nextGame.time} • {nextGame.location}
                  </div>
                  
                  <WeatherCard 
                    location={nextGame.location}
                    date={nextGame.date}
                    gameTime={nextGame.time}
                    loading={isLoading}
                  />
                </>
              ) : (
                <div className="text-center py-6">
                  <div className="text-gray-400 dark:text-gray-500 mb-2">
                    <Calendar size={24} className="mx-auto mb-2" />
                    No upcoming games scheduled
                  </div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Add games in the Schedule tab to see them here
                  </p>
                </div>
              )}
            </StatCard>
          </ErrorBoundary>
        </Section>
        {/* Milestones */}
        <Section title="Milestones & Achievements" icon={ICONS.ACHIEVEMENTS} defaultExpanded={true}>
          <ErrorBoundary>
            <MemoizedMilestonesCard loading={isLoading} />
          </ErrorBoundary>
        </Section>
        
        {/* Performance Trends - Only show if we have stats for the selected season */}
        {(battingStats?.games > 0 || pitchingStats?.games > 0) && (
          <Section title="Performance Trends" icon={ICONS.TRENDS} defaultExpanded={true}>
            <ErrorBoundary>
              <MemoizedPerformanceTrends 
                loading={isLoading} 
                playerData={playerData} 
                hasStats={battingStats?.games > 0 || pitchingStats?.games > 0}
              />
            </ErrorBoundary>
          </Section>
        )}
        
        {/* Performance Analysis */}
        <Section title="Performance Analysis" icon={ICONS.ALERT} defaultExpanded={true}>
          <ErrorBoundary>
            <MemoizedSlumpDetection 
              playerData={playerData}
              loading={isLoading}
            />
          </ErrorBoundary>
        </Section>
        
        {/* Recent Games - Filter by season if one is selected */}
        <Section title="Recent Games" icon={ICONS.GAMES} defaultExpanded={true}>
          <ErrorBoundary>
            <MemoizedRecentGames
              loading={isLoading}
              recentGames={!viewingAllSeasons && activeSeason
                ? (playerData?.seasonGames || [])
                : (playerData?.recentGames || [])}
            />
          </ErrorBoundary>
        </Section>
      </div>
    </>
  );
};
// PropTypes for Dashboard component
Dashboard.propTypes = {
  playerData: PropTypes.object,
  isLoading: PropTypes.bool,
  showQuickEntry: PropTypes.bool,
  setShowQuickEntry: PropTypes.func,
  fetchPlayerData: PropTypes.func
};

export default Dashboard;
